import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Dutch time scheduled logout times (HH:MM)
const SCHEDULED_LOGOUT_TIMES = ["10:00", "12:30", "15:00", "18:00", "23:00"];
const INACTIVITY_LIMIT_MS = 60 * 60 * 1000; // 1 hour
const CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds
const SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get current time in Europe/Amsterdam timezone as HH:MM string
 */
const getDutchTime = (): string => {
  const now = new Date();
  return now.toLocaleTimeString("nl-NL", {
    timeZone: "Europe/Amsterdam",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

/**
 * Central session guard hook that handles:
 * 1. Scheduled logouts at fixed Dutch times
 * 2. Inactivity logout after 1 hour
 * 3. Periodic last_activity sync to database
 */
export const useSessionGuard = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const lastActivityRef = useRef<number>(Date.now());
  const lastSyncRef = useRef<number>(Date.now());
  const lastCheckedTimeRef = useRef<string>("");
  const isLoggingOutRef = useRef(false);

  const performLogout = useCallback(
    async (reason: string) => {
      if (isLoggingOutRef.current || !isAuthenticated) return;
      isLoggingOutRef.current = true;

      console.log("[SessionGuard] Logging out:", reason);
      toast.info(reason, { duration: 5000 });

      try {
        await logout();
        navigate("/login");
      } catch (e) {
        console.error("[SessionGuard] Logout error:", e);
      } finally {
        isLoggingOutRef.current = false;
      }
    },
    [isAuthenticated, logout, navigate]
  );

  // Track user activity
  useEffect(() => {
    if (!isAuthenticated) return;

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, updateActivity, { passive: true }));

    return () => {
      events.forEach((e) => window.removeEventListener(e, updateActivity));
    };
  }, [isAuthenticated]);

  // Main guard loop: scheduled logouts + inactivity check + db sync
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const interval = setInterval(async () => {
      // 1. Scheduled logout check
      const dutchTime = getDutchTime();
      if (
        SCHEDULED_LOGOUT_TIMES.includes(dutchTime) &&
        lastCheckedTimeRef.current !== dutchTime
      ) {
        lastCheckedTimeRef.current = dutchTime;
        await performLogout(`Automatische uitlog om ${dutchTime} (gepland tijdstip)`);
        return;
      }
      // Reset so the same minute doesn't block future checks
      if (!SCHEDULED_LOGOUT_TIMES.includes(dutchTime)) {
        lastCheckedTimeRef.current = "";
      }

      // 2. Inactivity check
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= INACTIVITY_LIMIT_MS) {
        await performLogout("Automatische uitlog wegens 1 uur inactiviteit");
        return;
      }

      // 3. Sync last_activity to database periodically
      const sinceSyncMs = Date.now() - lastSyncRef.current;
      if (sinceSyncMs >= SYNC_INTERVAL_MS) {
        lastSyncRef.current = Date.now();
        const sessionToken = localStorage.getItem("auth_session_token");
        if (sessionToken) {
          await supabase
            .from("auth_sessions")
            .update({ last_activity: new Date().toISOString() })
            .eq("session_token", sessionToken)
            .eq("is_active", true);
        }
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isAuthenticated, user, performLogout]);
};
